import { parseTranscript, getRawTranscript } from '../utils/transcriptParser'
// In TranscriptionsPage.js, import the utility at the top
// src/pages/TranscriptionsPage.js - Updated download function
import { useState, useEffect } from "react"
import { FileText, Search, Download, Eye, Phone, User, Calendar, Clock } from "lucide-react"
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const TranscriptionsPage = () => {
  const [transcriptions, setTranscriptions] = useState([])
  const [selectedTranscript, setSelectedTranscript] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    fetchTranscriptions()
  }, [])

  const fetchTranscriptions = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await fetch("https://call-analyzer-backend.onrender.com/user/transcriptions", {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setTranscriptions(data.transcriptions)
      }
    } catch (error) {
      console.error("Failed to fetch transcriptions:", error)
    } finally {
      setLoading(false)
    }
  }

  // Format transcript into speaker turns
  

  // Download transcript as PDF
  const downloadTranscript = () => {
    if (!selectedTranscript) return
    
    const doc = new jsPDF()
    const formatted = parseTranscript(selectedTranscript.transcript)
    
    // Colors
    const primaryColor = [10, 75, 75] // NeuroTrain brand color
    const blueColor = [37, 99, 235] // Blue for agent
    const grayColor = [75, 85, 99] // Gray for customer
    
    // Header with brand colors
    doc.setFillColor(...primaryColor)
    doc.rect(0, 0, 210, 35, 'F')
    
    // Title
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(20)
    doc.setFont(undefined, 'bold')
    doc.text('NeuroTrain Call Transcript', 14, 18)
    
    doc.setFontSize(12)
    doc.setFont(undefined, 'normal')
    doc.text('Professional Transcription Service', 14, 28)
    
    // Document info section
    let yPosition = 45
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(11)
    doc.setFont(undefined, 'bold')
    doc.text('Call Information', 14, yPosition)
    
    yPosition += 8
    doc.setFont(undefined, 'normal')
    doc.setFontSize(10)
    
    // Info table
    const infoData = [
      ['Agent Name:', selectedTranscript.agent_name],
      ['Date:', new Date(selectedTranscript.date).toLocaleDateString()],
      ['Time:', new Date(selectedTranscript.date).toLocaleTimeString()],
      ['File:', selectedTranscript.file_name || 'Audio Recording'],
      ['Total Exchanges:', formatted.length.toString()]
    ]
    
    doc.autoTable({
      startY: yPosition,
      head: [],
      body: infoData,
      theme: 'plain',
      styles: {
        fontSize: 10,
        cellPadding: 2
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 35 },
        1: { cellWidth: 100 }
      }
    })
    
    yPosition = doc.lastAutoTable.finalY + 15
    
    // Transcript header
    doc.setFontSize(11)
    doc.setFont(undefined, 'bold')
    doc.text('Call Transcript', 14, yPosition)
    
    yPosition += 10
    
    // Horizontal line
    doc.setDrawColor(200, 200, 200)
    doc.line(14, yPosition - 3, 196, yPosition - 3)
    
    // Add conversation turns
    if (formatted.length === 0) {
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.text('Transcript format not recognized. Raw transcript:', 14, yPosition)
      yPosition += 8

      const rawLines = doc.splitTextToSize(getRawTranscript(selectedTranscript.transcript), 175)
      rawLines.forEach(line => {
        if (yPosition > 270) {
          doc.addPage()
          yPosition = 20
        }
        doc.text(line, 14, yPosition)
        yPosition += 5
      })
    } else {
formatted.forEach((turn, index) => {
      // Check for page break
      if (yPosition > 260) {
        // Add page number
        doc.setFontSize(8)
        doc.setTextColor(150, 150, 150)
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, 105, 285, { align: 'center' })
        
        doc.addPage()
        yPosition = 20
      }
      
      // Speaker label with color
      doc.setFont(undefined, 'bold')
      doc.setFontSize(10)
      
      if (turn.type === 'agent') {
        doc.setTextColor(...blueColor)
        doc.text('AGENT:', 14, yPosition)
      } else {
        doc.setTextColor(...grayColor)
        doc.text('CUSTOMER:', 14, yPosition)
      }
      
      yPosition += 5
      
      // Message text
      doc.setFont(undefined, 'normal')
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(9)
      
      // Wrap text
      const lines = doc.splitTextToSize(turn.text, 175)
      lines.forEach(line => {
        if (yPosition > 270) {
          // Add page number
          doc.setFontSize(8)
          doc.setTextColor(150, 150, 150)
          doc.text(`Page ${doc.internal.getNumberOfPages()}`, 105, 285, { align: 'center' })
          
          doc.addPage()
          yPosition = 20
        }
        doc.text(line, 20, yPosition)
        yPosition += 4
      })
      
      yPosition += 4 // Extra space between turns
    })
    
    }
    // Footer on last page
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.setDrawColor(200, 200, 200)
    doc.line(14, 275, 196, 275)
    
    doc.text('Generated by NeuroTrain Call Analyzer', 14, 280)
    doc.text(new Date().toLocaleString(), 196, 280, { align: 'right' })
    doc.text(`Page ${doc.internal.getNumberOfPages()}`, 105, 285, { align: 'center' })
    
    // Add metadata
    doc.setProperties({
      title: `Transcript - ${selectedTranscript.agent_name}`,
      subject: 'Call Transcript',
      author: 'NeuroTrain',
      keywords: 'transcript, call, analysis',
      creator: 'NeuroTrain Call Analyzer'
    })
    
    // Save the PDF
    const fileName = `transcript_${selectedTranscript.agent_name}_${new Date(selectedTranscript.date).toISOString().split('T')[0]}.pdf`
    doc.save(fileName)
  }

  const filteredTranscriptions = transcriptions.filter(t => 
    t.agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.transcript?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Call Transcriptions</h1>
          <p className="text-gray-600 mt-2">View and download professional PDF transcripts</p>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by agent name or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Transcription List - Narrower */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6 max-h-[700px] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Transcripts</h2>
            
            {filteredTranscriptions.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No transcripts found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTranscriptions.map((transcript) => (
                  <div
                    key={transcript.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedTranscript?.id === transcript.id 
                        ? 'border-blue-500 bg-blue-50 shadow-md' 
                        : 'hover:bg-gray-50 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedTranscript(transcript)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-500" />
                          {transcript.agent_name}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(transcript.date).toLocaleDateString()}
                        </p>
                        {transcript.file_name && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            {transcript.file_name}
                          </p>
                        )}
                      </div>
                      <Eye className={`h-5 w-5 ${
                        selectedTranscript?.id === transcript.id ? 'text-blue-500' : 'text-gray-400'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Transcript Viewer - Wider */}
          <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6 pb-4 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Transcript Viewer</h2>
                {selectedTranscript && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedTranscript.agent_name} • {new Date(selectedTranscript.date).toLocaleDateString()}
                  </p>
                )}
              </div>
              {selectedTranscript && (
                <button
                  onClick={downloadTranscript}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </button>
              )}
            </div>
            
            {selectedTranscript ? (
              <div className="max-h-[580px] overflow-y-auto">
                {/* Formatted Transcript */}
                <div className="space-y-4">
                  {parseTranscript(selectedTranscript.transcript).map((turn, index) => (
                    <div
                      key={index}
                      className={`rounded-lg p-4 ${
                        turn.type === 'agent' 
                          ? 'bg-blue-50 border-l-4 border-blue-500' 
                          : 'bg-gray-50 border-l-4 border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {turn.type === 'agent' ? (
                          <>
                            <Phone className="h-4 w-4 text-blue-600" />
                            <span className="font-semibold text-blue-900">Agent</span>
                          </>
                        ) : (
                          <>
                            <User className="h-4 w-4 text-gray-600" />
                            <span className="font-semibold text-gray-900">Customer</span>
                          </>
                        )}
                      </div>
                      <p className="text-gray-700 leading-relaxed">{turn.text}</p>
                    </div>
                  ))}
                  
                  {parseTranscript(selectedTranscript.transcript).length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <p>No formatted transcript available</p>
                      <p className="text-sm mt-2">Raw transcript:</p>
                      <pre className="mt-4 text-left whitespace-pre-wrap text-xs bg-gray-50 p-4 rounded">
                        {selectedTranscript.transcript}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-24">
                <FileText className="h-20 w-20 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Select a transcript to view</p>
                <p className="text-sm mt-2">Choose from the list on the left</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-6 bg-white rounded-xl shadow-lg p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{transcriptions.length}</p>
              <p className="text-sm text-gray-600">Total Transcripts</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {selectedTranscript ? parseTranscript(selectedTranscript.transcript).length : 0}
              </p>
              <p className="text-sm text-gray-600">Conversation Turns</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {selectedTranscript ? 
                  parseTranscript(selectedTranscript.transcript).filter(t => t.type === 'agent').length : 0}
              </p>
              <p className="text-sm text-gray-600">Agent Messages</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">
                {selectedTranscript ? 
                  parseTranscript(selectedTranscript.transcript).filter(t => t.type === 'customer').length : 0}
              </p>
              <p className="text-sm text-gray-600">Customer Messages</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TranscriptionsPage
